import { memo, useCallback, useContext, useEffect, useState } from 'react';
import { Modal } from '../../../components/Modal';
import { RendererContext } from '../../../contexts/RendererContext';
import { T } from '../../../translation/src';

interface WallSourcePromptEvent {
  floorGroupIdentifier?: string;
}

/** The two values of the floor's `floor_wall_source` property. */
type WallSourceChoice = 'ROOMS' | 'WALL_SKETCH';

interface FloorplanPersonaWithWallSourcePrompt {
  name: string;
  addEventListener(event: string, handler: (e: WallSourcePromptEvent) => void): void;
  removeEventListener(event: string, handler: (e: WallSourcePromptEvent) => void): void;
  chooseWallSource(source: WallSourceChoice): void;
  dismissWallSourcePrompt(): void;
}

/**
 * Mounts the dialog shown when the floorplan persona's Wall tab is entered
 * (or its floor selection changes) while the selected floor has no wall
 * sketch yet: the user picks the floor's wall representation — Standardised
 * (the floor's `floor_wall_source` becomes WALL_SKETCH) or As measured
 * (ROOMS). One big stacked button per choice, title + description inside the
 * clickable area. Dismissing writes nothing, leaving the property default in
 * place.
 *
 * Subscribes to `wall_source_prompt` / `wall_source_prompt_dismiss` events on
 * the floorplan persona (see FloorplanPersona.maybePromptWallSource).
 */
const OPTION_BUTTON_CLASS =
  'w-full text-left px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer transition-colors hover:border-neon-green-600 dark:hover:border-neon-green-300 hover:bg-neon-green-50 dark:hover:bg-neon-green-300/10';
const WallSourcePrompt_: React.FC = () => {
  const { viewer } = useContext(RendererContext);
  const [open, setOpen] = useState(false);

  const getPersona = useCallback((): FloorplanPersonaWithWallSourcePrompt | undefined => {
    return viewer?.personaManager?.getAllPersonas().find((p: { name: string }) => p.name === 'floorplan') as FloorplanPersonaWithWallSourcePrompt | undefined;
  }, [viewer]);

  useEffect(() => {
    const persona = getPersona();
    if (!persona) return;

    const onPrompt = () => setOpen(true);
    const onDismiss = () => setOpen(false);

    persona.addEventListener('wall_source_prompt', onPrompt);
    persona.addEventListener('wall_source_prompt_dismiss', onDismiss);

    return () => {
      persona.removeEventListener('wall_source_prompt', onPrompt);
      persona.removeEventListener('wall_source_prompt_dismiss', onDismiss);
    };
  }, [getPersona]);

  const handleChoose = useCallback(
    (source: WallSourceChoice) => {
      getPersona()?.chooseWallSource(source);
      setOpen(false);
    },
    [getPersona],
  );

  const handleClose = useCallback(() => {
    getPersona()?.dismissWallSourcePrompt();
    setOpen(false);
  }, [getPersona]);

  return (
    <Modal.Container open={open} onClose={handleClose} panelClassName="max-w-md">
      <Modal.Header className="!mb-2">
        <span className="font-semibold">
          <T _str="how should the walls be represented?" swc />
        </span>
      </Modal.Header>
      <p className="text-sm text-gray-700 dark:text-gray-300">
        <T _str="choose how the wall geometry should be represented. you can change this later." swc />
      </p>
      <div className="flex flex-col gap-3 mt-4">
        <button type="button" className={OPTION_BUTTON_CLASS} onClick={() => handleChoose('WALL_SKETCH')}>
          <span className="block font-semibold">
            <T _str="standardised" swc />
          </span>
          <span className="block mt-1 text-sm text-gray-700 dark:text-gray-300">
            <T _str="straighten wall angles and use consistent wall widths to create a clean, editable floor plan." swc />
          </span>
        </button>
        <button type="button" className={OPTION_BUTTON_CLASS} onClick={() => handleChoose('ROOMS')}>
          <span className="block font-semibold">
            <T _str="as-built" swc />
          </span>
          <span className="block mt-1 text-sm text-gray-700 dark:text-gray-300">
            <T _str="keep the wall geometry as measured from the rooms." swc />
          </span>
        </button>
      </div>
    </Modal.Container>
  );
};

export const WallSourcePrompt = memo(WallSourcePrompt_);
